import { Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges } from '@angular/core';
import { UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { CountryModel, StateModel } from '@data-layer/models/common';
import { CompanyModel } from '@data-layer/models/companies';
import { FormHelper } from '@helpers/form.helper';
import { CompaniesService } from '@services/companies.service';
import { TranslateService } from '@ngx-translate/core';
import { BondRequestRepository, CodeRepository, CompanyBondsRepository } from '@data-layer/repositories';
import { DocumentsService } from '@services/documents.service';
import { enumBondRequestState, enumRespErrors } from '@data-layer/enums/common';
import { catchError, finalize, map, switchMap, take, takeUntil, tap } from 'rxjs/operators';
import { EMPTY, Observable, of, ReplaySubject, throwError } from 'rxjs';
import {
  enumDocumentRelatedEntityType,
  enumDocumentRootEntityType,
  enumDocumentState,
  enumDocumentType,
} from '@data-layer/enums/documents';
import { CUSTOM_MASK } from '@helpers/custom-validator';
import { enumCompanyBusinessType } from '@data-layer/enums/companies';
import { AlertService } from '@services/alert.service';

@Component({
  selector: 'app-company-bond-request-form',
  templateUrl: './company-bond-request-form.component.html',
  styleUrls: ['./company-bond-request-form.component.scss'],
})
export class CompanyBondRequestFormComponent implements OnInit, OnDestroy, OnChanges {
  @Input() public company: CompanyModel;
  @Output() public afterSave: EventEmitter<any> = new EventEmitter<any>();

  public formGroup: UntypedFormGroup;
  public poaFormControl: UntypedFormControl = new UntypedFormControl(null, [Validators.required]);
  public errors = {
    required: 'FORM_ERROR.REQUIRED',
  };
  public CUSTOM_MASK = CUSTOM_MASK;
  public initialCountry: CountryModel[];
  public initialState: StateModel[];
  public isLoadingCompanyPoa: boolean = false;
  public isShowPoaForm: boolean = false;
  public error: string;

  private destroy$: ReplaySubject<void> = new ReplaySubject<void>();

  constructor(
    private fh: FormHelper,
    private companiesService: CompaniesService,
    private translateService: TranslateService,
    private codeRepository: CodeRepository,
    private bondRequestRepository: BondRequestRepository,
    private documentsService: DocumentsService,
    private companyBondsRepository: CompanyBondsRepository,
    private alertService: AlertService,
  ) {}

  public ngOnInit(): void {
    this.getCompanyPoaDocument()
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => (this.isShowPoaForm = !this.poaFormControl.value));
  }

  public ngOnChanges(changes: SimpleChanges): void {
    if (changes.company) {
      this.formGroup = this.fh.createCompanyBondPurchaseFormGroup();
      this.formGroup.get(['continuousBondDetails', 'companyName']).disable();
      this.formGroup.get(['continuousBondDetails', 'address']).disable();
      this.formGroup.get(['continuousBondDetails', 'importerNumber']).disable();
      this.formGroup.get(['continuousBondDetails', 'businessType']).disable();
      this.fillForm();
    }
  }

  public ngOnDestroy(): void {
    this.destroy$.next();
  }

  public get businessTypeLabel(): string {
    return enumCompanyBusinessType[this.formGroup.get(['continuousBondDetails', 'businessType']).value];
  }

  public applyForm() {
    if (this.formGroup.invalid || this.poaFormControl.invalid) {
      this.fh.markAsTouched(this.formGroup);
      this.fh.markAsTouched(this.poaFormControl);
      this.scrollToFirstUserError();
      return;
    }

    this.getBondRequest()
      .pipe(
        takeUntil(this.destroy$),
        switchMap(() => this.companiesService.getCompanyAsync(true)),
      )
      .subscribe(() => {
        this.afterSave.emit();
      });
  }

  public getBondRequest(): Observable<string> {
    const formValue = this.formGroup.getRawValue();

    // allow to update BR only in Draft state
    if (!!this.company.bondRequest && [enumBondRequestState.CREATED].includes(this.company.bondRequest.state)) {
      return this.bondRequestRepository.update(this.company.bondRequest.id, formValue).pipe(
        map((value) => {
          return value.referenceNumber;
        }),
        catchError((error) => {
          return throwError(error);
        }),
      );
    }

    return this.disconnectCompletedBondRequest().pipe(
      switchMap(() => this.bondRequestRepository.create(this.company.id, formValue)),
      // a failed create never persists a bond request, so there is nothing to delete
      catchError(() => EMPTY),
      switchMap((value) => {
        return this.companyBondsRepository.connectBondRequest(this.company.id, value.id).pipe(
          map(() => value.referenceNumber),
          catchError((error) => {
            if (!error?.error?.tag || error?.error?.tag !== enumRespErrors.BondAlreadySet) {
              this.bondRequestRepository
                .delete(value.id)
                .pipe(take(1))
                .subscribe(() => {
                  this.company.bondRequest = undefined;
                });
            }

            return throwError(error);
          }),
        );
      }),
    );
  }

  public searchCountry() {
    return this.codeRepository.searchCountry.bind(this.codeRepository);
  }

  public searchState() {
    return this.codeRepository.searchState.bind(this.codeRepository, 'US');
  }

  public updateDocumentState(state: enumDocumentState) {
    this.poaFormControl.patchValue({ ...this.poaFormControl.value, state });
  }

  public uploadPoa(file: File) {
    this.isLoadingCompanyPoa = true;
    this.documentsService
      .uploadDocument(
        {
          fileName: file.name,
          relatedEntityId: this.company.id,
          relatedEntityType: enumDocumentRelatedEntityType.COMPANY,
          rootEntityId: this.company.id,
          rootEntityType: enumDocumentRootEntityType.COMPANY,
          type: enumDocumentType.POWER_OF_ATTORNEY,
        },
        file,
      )
      .pipe(
        take(1),
        switchMap(() => this.getCompanyPoaDocument(false)),
        finalize(() => (this.isLoadingCompanyPoa = false)),
      )
      .subscribe();
  }

  private disconnectCompletedBondRequest() {
    if (
      this.company.bondRequest &&
      [enumBondRequestState.COMPLETED, enumBondRequestState.CUSTOM].includes(this.company.bondRequest.state)
    ) {
      return this.companyBondsRepository.disconnectBondRequest(this.company.id);
    }
    return of(null);
  }

  private getCompanyPoaDocument(isShowLoader: boolean = true) {
    return this.documentsService.getCompanyDocuments(this.company.id, isShowLoader).pipe(
      tap((documents) => {
        this.poaFormControl.patchValue(documents.find((document) => document.type === enumDocumentType.POWER_OF_ATTORNEY));
      }),
    );
  }

  private fillForm() {
    const bondRequest = this.company.bondRequest;
    if (bondRequest && bondRequest.state === enumBondRequestState.CREATED) {
      if (bondRequest && bondRequest.continuousBondDetails && bondRequest.continuousBondDetails.mainCountryOfOriginCode) {
        this.fillCountry();
      }
      if (bondRequest && bondRequest.continuousBondDetails && bondRequest.continuousBondDetails.stateOfIncorporationCode) {
        this.fillState();
      }
      this.formGroup.patchValue(this.company?.bondRequest);
    }

    const continuousBondDetails = this.formGroup.get('continuousBondDetails');

    continuousBondDetails.get('companyName').patchValue(this.company?.businessName);
    continuousBondDetails.get('businessType').patchValue(this.company?.businessType);

    const address: string = this.companiesService.getCompanyAddressLine(this.company);

    continuousBondDetails.get('address').patchValue(address);

    continuousBondDetails
      .get('importerNumber')
      .patchValue(
        [this.translateService.instant('IDENTIFIER_QUALIFIER.' + this.company.identifierQualifier), this.company.identifier].join(' '),
      );
  }

  private fillCountry() {
    this.codeRepository
      .getCountry(this.company.bondRequest.continuousBondDetails.mainCountryOfOriginCode)
      .pipe(take(1))
      .subscribe((country) => (this.initialCountry = [country]));
  }

  private fillState() {
    this.codeRepository
      .getState(this.company.bondRequest.continuousBondDetails.stateOfIncorporationCode)
      .pipe(take(1))
      .subscribe((state) => (this.initialState = [state]));
  }

  private scrollToFirstUserError() {
    if (this.poaFormControl.invalid) {
      this.navigateToSection('control-poaForm');
      return;
    }
    for (const controlName in this.formGroup.get('continuousBondDetails').value) {
      if (
        this.formGroup.get(['continuousBondDetails', controlName]) &&
        this.formGroup.get(['continuousBondDetails', controlName]).invalid
      ) {
        const sectionId = 'control-' + controlName;
        this.navigateToSection(sectionId);
        return;
      }
    }
  }

  private navigateToSection(fragmentId: string) {
    const element = document.getElementById(fragmentId);
    if (element && element.scrollIntoView) {
      element.scrollIntoView({
        behavior: 'smooth',
        block: 'center',
      });
    }
  }
}
